/* eslint-disable no-unused-vars */
import * as d3 from 'd3';
import _ from 'lodash';
import { intcomma, apmonth, apdate, pluralize } from 'journalize';
import {
  movingAvg,
  movingAvgSubtractMethod,
  customDateFormat,
  customMobileDateFormatFour,
  customNumFormat,
  getLabeledData,
  mergeArrays,
} from '../utils/charts-utils';
import * as moment from 'moment';
// import { swoopyDrag } from '../swoopy-drag';
// import { wordwrap } from 'd3-jetpack';

// import data
import data from '../../../data/daily_tables/all_tables.json';
import colors from '../../../data/chart-colors.json';

// a reference to the default graphic container, change if needed
const container = d3.select('#covid-tests');

// a helper function to grab the container's width
const getFrameWidth = () => container.node().offsetWidth;

//set margins
let margin = {
  top: 15,
  left: 50,
  bottom: 40,
};

let breakPoint = 500;

// grabbing totals
let dataTotals = [];

// NOTE: THIS IS TEMPORARILY ADDED
// to account for them not having testing data on 5/24
// data['yesterday_date'] = '05_22_2020';

// If we have incomplete data for the yesterday's date, let's use the day before
// yesterday's date is what we use for the positivity rate
let cases_latest_date = data['cases_latest_date'];

let yesterday = moment(data['cases_latest_date'], 'M_D_YYYY')
  .subtract(1, 'days')
  .format('MM_DD_YYYY');
let yesterday_incomplete = data[yesterday].statewide_data.incomplete;

if (yesterday_incomplete != undefined) {
  if (
    yesterday_incomplete.includes('viral_tests_diff') ||
    yesterday_incomplete.includes('antibody_tests_diff') ||
    yesterday_incomplete.includes('total_tests_diff')
  ) {
    cases_latest_date = yesterday;
  }
}

// there are six properties prior that we want to exclude: 'latest_date', 'yesterday_date', 'week_ago', 'cases_latest_date', 'cases_yesterday_date', 'cases_week_ago', 'cases_yesterday_date'
for (let i = Object.keys(data).length - 1; i > 6; i--) {
  if (
    moment(Object.keys(data)[i], 'M_D_YYYY').isBefore(
      moment(cases_latest_date, 'M_D_YYYY')
    )
  ) {
    dataTotals.push({
      incomplete: data[Object.keys(data)[i - 1]].statewide_data.incomplete,
      unformatted_date: Object.keys(data)[i - 1],
      total_tests: data[Object.keys(data)[i - 1]].statewide_data.total_tests,
      new_tests:
        data[Object.keys(data)[i - 1]].statewide_data.total_tests_diff &&
        data[Object.keys(data)[i - 1]].statewide_data.total_tests_diff != ''
          ? data[Object.keys(data)[i - 1]].statewide_data.total_tests_diff
          : 0, // tests were not reported before 3/17, so we sub in a 0
      viral_tests:
        data[Object.keys(data)[i - 1]].statewide_data.viral_tests_diff < 0
          ? 0
          : data[Object.keys(data)[i - 1]].statewide_data.viral_tests_diff,
      antibody_tests:
        data[Object.keys(data)[i - 1]].statewide_data.antibody_tests_diff < 0
          ? 0
          : data[Object.keys(data)[i - 1]].statewide_data.antibody_tests_diff,
    });
  }
}

// changing to moment date+time objects
dataTotals.forEach(d => (d.date = moment(d.unformatted_date, 'M_D_YYYY')));

// Take out the last day because we won't have total
dataTotals = dataTotals.slice(0, -1);

// Add an id based on index to use for joining
dataTotals.forEach((d, i) => (d.id = i));

// any days that had missing data
let missedDates = dataTotals.filter(d => {
  if (
    d.antibody_tests == 'no_data' ||
    d.viral_tests == 'no_data' ||
    d.new_tests == 'no_data' ||
    d.new_tests < 0
  ) {
    return true;
  }
});

// Make object into array
let dataArray = dataTotals.map(d => d['total_tests']);
let dataMovingAvg = movingAvgSubtractMethod(dataArray, 7, 'moving_avg');

dataTotals = mergeArrays(dataTotals, dataMovingAvg, 'id');

// grabbing totals and ranges
let dateRange = d3.extent(dataTotals, d => d.date),
  totalsMax = d3.max(dataTotals, d => d.new_tests);

// set ranges
let x = d3.scaleTime().domain(dateRange);
let y = d3
  .scaleLinear()
  .domain([0, totalsMax])
  .nice();

// append svg
let svg = container.append('svg'),
  g = svg.append('g').attr('class', 'line-chart'),
  labels_g = svg.append('g').attr('class', 'labels-container');

let diagonalStripePattern = svg
  .append('defs')
  .append('pattern')
  .attr('id', 'diagonal-stripe')
  .attr('patternUnits', 'userSpaceOnUse')
  .attr('patternTransform', 'rotate(45 0 0)')
  .attr('width', 4)
  .attr('height', 4);

diagonalStripePattern
  .append('rect')
  .attr('width', 4)
  .attr('height', 4)
  .style('fill', colors.bar_default);

diagonalStripePattern
  .append('line')
  .attr('x1', 0)
  .attr('y1', 0)
  .attr('x2', 0)
  .attr('y2', 4)
  .style('stroke', 'black')
  .style('stroke-width', 1);

let xAxis = g.append('g').attr('class', 'axis axis--x'),
  yAxis = g.append('g').attr('class', 'axis axis--y');

let line = d3
  .line()
  .defined(d => !isNaN(d.moving_avg))
  .x(d => x(d.date))
  .y(d => y(d.moving_avg));

dataTotals.filter(line.defined());

// Get the new starting date because the state did not provide comprehensive deaths data before then
const start_date = dataTotals.filter(d => d.unformatted_date == '05_14_2020');

// Filter out everything before the new starting date
const dataTotals2 = dataTotals.filter(d => d.date < start_date[0].date);
const dataTotals3 = dataTotals.filter(d => {
  return (
    d.date >= start_date[0].date &&
    !missedDates.map(d => d.date).includes(d.date) // filter out any dates that have missing test data
  );
});

let bars = g
  .selectAll('.bar')
  .data(dataTotals2)
  .enter()
  .append('rect')
  .attr('class', 'bar')
  .attr('fill', colors.bar_neutral)
  .attr('opacity', 1);

let viral_bars = g
  .selectAll('.viral_bar')
  .data(dataTotals3)
  .enter()
  .append('rect')
  .attr('class', 'viral_bar')
  .attr('fill', d => {
    if (d.incomplete && d.incomplete.includes('viral_tests_diff'))
      // color incomplete bars gray
      return colors.bar_neutral;
    else return colors.bar_viral;
  })
  .attr('opacity', 1);

let antibody_bars = g
  .selectAll('.antibody_bar')
  .data(dataTotals3)
  .enter()
  .append('rect')
  .attr('class', 'antibody_bar')
  .attr('fill', d => {
    if (d.incomplete && d.incomplete.includes('antibody_tests_diff'))
      // color incomplete bars gray
      return colors.bar_neutral;
    else return colors.bar_antibody;
  })
  .attr('opacity', 1);

// days that did not have the antibody and viral breakouts
// Note: DSHS changed its viral tests numbers for June 6 so that the total number of viral tests actually decreased instead of increased giving us a negative daily viral tests so we have to make it not visible.
let missed_day_bars = g
  .selectAll('.missed_day_bar')
  .data(missedDates)
  .enter()
  .append('rect')
  .attr('class', 'missed_day_bar')
  .style('fill', d => {
    return d.unformatted_date == '06_06_2020' ? '#FFFFFF' : colors.bar_neutral;
  })
  .attr('opacity', 1);

// filter out days with no moving average
let dataTotalsNoMovingAvg = dataTotals.filter(d => {
  if (
    isNaN(d.moving_avg) ||
    d.moving_avg < 0 ||
    d.incomplete == '7_day_avg_tests'
  ) {
    return false;
  } else {
    return true;
  }
});

let pathOutline = g
  .append('path')
  .data([dataTotalsNoMovingAvg])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', '#fff')
  .attr('stroke-width', 4);

let path = g
  .append('path')
  .data([dataTotalsNoMovingAvg])
  .attr('class', 'line')
  .attr('fill', 'none')
  .attr('stroke', colors.line_moving_avg)
  .attr('stroke-width', 2);

const day_for_label = [[dataTotalsNoMovingAvg][0][6]]; // Gets the day that where the 7-day average label will be next to

let moving_avg_label = g.append('g').attr('class', 'moving-avg-label');

let moving_avg_g = moving_avg_label
  .selectAll('g')
  .data(day_for_label)
  .enter()
  .append('g');

let moving_avgText = moving_avg_g.append('g').attr('class', 'label-text');

moving_avgText
  .append('text')
  .text('7-day average')
  .attr('fill', colors.line_moving_avg)
  .attr('font-weight', 700);

let labels = labels_g.append('g').attr('class', 'labeled-elements');

let labelElements = labels
  .selectAll('g')
  .data(getLabeledData(dataTotalsNoMovingAvg, ['']))
  .enter()
  .append('g')
  .attr('class', 'label-element');

let labelText = labelElements.append('g').attr('class', 'label-text');

labelText
  .append('text')
  .text((d, i) => {
    if (i === 0) {
      let month = apmonth(new Date(d.date)),
        day = d.date.format('D');

      return `${month} ${day}`;
    } else return '';
  })
  .attr('font-weight', 700);

let casesLabel = labelText
  .append('text')
  .attr('fill', colors.line_moving_avg)
  .text(d => {
    return `${intcomma(Math.round(d.moving_avg))} test${pluralize(
      d.moving_avg
    )}`;
  });

// Goal annotation
let events = g.append('g').attr('class', 'events');

let eventGroups = events
  .selectAll('g')
  .data([
    {
      event_desktop: 'Goal: 30,000 tests',
      event_mobile: 'Goal: 30,000',
      rate_threshold: 30000,
    },
  ])
  .enter()
  .append('g');

let eventText = eventGroups.append('text');

let eventLines = eventGroups
  .append('line')
  .attr('stroke', '#787878')
  .attr('stroke-width', '1px')
  .attr('stroke-dasharray', 5);

function resize() {
  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth < breakPoint) {
    frameHeight = 375;
    margin.right = 75;
  } else {
    frameHeight = 425;
    margin.right = 95;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );
  labels_g.attr('transform', `translate(${margin.left},${margin.top})`);

  let barWidth = chartWidth / dataTotals.length;

  x.range([barWidth / 1.5, chartWidth - barWidth / 1.5]);
  y.range([chartHeight, 0]);

  bars
    .attr('width', barWidth - barWidth / 5)
    .attr('x', d => x(d.date) - barWidth / 2)
    .attr('y', d => y(d.new_tests))
    .attr('height', d => chartHeight - y(d.new_tests));

  antibody_bars
    .attr('width', barWidth - barWidth / 5)
    .attr('x', d => x(d.date) - barWidth / 2)
    .attr('y', d => y(d.antibody_tests))
    .attr('height', d => chartHeight - y(d.antibody_tests));

  viral_bars
    .attr('width', barWidth - barWidth / 5)
    .attr('x', d => x(d.date) - barWidth / 2)
    .attr('y', d => y(d.antibody_tests) - (chartHeight - y(d.viral_tests)))
    .attr('height', d => chartHeight - y(d.viral_tests));

  missed_day_bars
    .attr('width', barWidth - barWidth / 5)
    .attr('x', d => x(d.date) - barWidth / 2)
    .attr('y', d => y(d.new_tests))
    .attr('height', d => {
      if (d.new_tests == 'no_data' || d.new_tests < 0) return 0;
      else return chartHeight - y(d.new_tests);
    });

  xAxis
    .call(
      d3
        .axisBottom(x)
        .ticks(frameWidth < breakPoint ? 10 : 15)
        .tickFormat((d, i) => {
          return frameWidth < breakPoint
            ? customMobileDateFormatFour(d, i)
            : customDateFormat(d, i, 2);
        })
    )
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(5)
      .tickSize(-chartWidth)
      .tickFormat(customNumFormat)
  );

  pathOutline.attr('d', line);
  path.attr('d', line);

  moving_avg_g
    .attr('transform', d => `translate(${x(d.date)},${y(d.moving_avg)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  moving_avgText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(0, -11)';
      } else return 'translate(0, -11)';
    })
    .attr('text-anchor', 'start');

  labelElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.moving_avg)})`)
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

  labelText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth < breakPoint) {
        return 'translate(9, 0)';
      } else return 'translate(15, 0)';
    })
    .attr('text-anchor', 'start');

  casesLabel.attr('dy', frameWidth < breakPoint ? 16 : 17);

  // Goal annotation
  eventGroups.attr('transform', d => `translate(0,${y(d.rate_threshold)})`);

  eventLines.attr('x1', 0).attr('x2', chartWidth);

  eventText
    .attr('class', 'label-text font-weight-bold')
    .text(d => (frameWidth < breakPoint ? d.event_mobile : d.event_desktop))
    .attr('font-size', frameWidth < breakPoint ? '12px' : '13px')
    .attr('dx', frameWidth < breakPoint ? 5 : 10)
    .attr('dy', frameWidth < breakPoint ? -7 : -10);
}

// call resize on load
resize();
window.addEventListener('resize', _.debounce(resize, 200));

// OUTSIDE OF RESIZE()

// let events = g.append('g').attr('class', 'events');

// let eventGroups = events
//   .selectAll('g')
//   .data([
//     {
//       event_desktop: 'San Antonio opens its first drive-thru testing site',
//       event_mobile: 'Bush',
//       date: '3_13',
//     },
//   ])
//   .enter()
//   .append('g');

// let eventText = eventGroups.append('text');

// let eventLines = eventGroups
//   .append('line')
//   .attr('stroke', '#d1d1d1')
//   .attr('stroke-width', '1px')
//   .attr('stroke-dasharray', 5);

// let metaInfo = g
//   .append('g')
//   .attr('class', 'meta')
//   .attr('font-size', '12px')
//   .attr('fill', 'hsl(0, 0%, 47%)');

// this is the same function findChartMeta() in nunjucks.js
// find a way to import that function here
// metaInfo
//   .append('text')
//   .text(`Note: ${data.Meta.find(d => d.Chart === 'Line').Note}`)
//   .attr('y', 45);

// metaInfo
//   .append('text')
//   .html(
//     `Source: <a style="fill: hsl(0, 0%, 47%); text-decoration: underline;"href="https://www.cbp.gov/sites/default/files/assets/documents/2019-Mar/bp-total-monthly-apps-sector-area-fy2018.pdf">${
//       data.Meta.find(d => d.Chart === 'Line').Source
//     }</a>`
//   )
//   .attr('y', 65);

//arrow marker, need to append to the svg first to use
// svg
//   .append('marker')
//   .attr('id', 'arrow')
//   .attr('viewBox', '-10 -10 20 20')
//   .attr('markerWidth', 10)
//   .attr('markerHeight', 10)
//   .attr('fill', 'hsl(0, 0%, 29%)')
//   .attr('orient', 'auto-start-reverse')
//   .append('path')
//   .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

// let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

// //annotations for swoopyDrag
// let annotations = [
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: 'M-25,-19C-32,-38,-47,-49,-63,-50',
//     text: 'March 24',
//     weight: 700,
//     textOffset: [-186, -77],
//   },
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: '',
//     text: 'State changed how it reports numbers.',
//     weight: 400,
//     textOffset: [-186, -63],
//   },
// ];

// let mobileAnnotations = [
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: 'M-12,-13C-30,-13,-42,-24,-46,-39',
//     text: 'March 24',
//     weight: 700,
//     textOffset: [-105, -75],
//   },
//   {
//     xVal: moment('3_24', 'M_D'),
//     yVal: 500,
//     path: '',
//     text: 'State changed how it reports numbers.',
//     weight: 400,
//     textOffset: [-105, -62],
//   },
// ];

// INSIDE OF RESIZE()

// eventGroups.attr(
//   'transform',
//   d => `translate(${(0, x(moment(d.date, 'M_D')))})`
// );

// eventLines.attr('y1', 5).attr('y2', chartHeight);

// eventText
//   .text(d => (frameWidth < breakPoint ? d.event_mobile : d.event_desktop))
//   .attr('font-size', frameWidth < breakPoint ? '12px' : '14px')
//   .attr('dx', frameWidth < breakPoint ? 5 : 10)
//   .attr('dy', frameWidth < breakPoint ? 14 : 20);

// metaInfo.attr('transform', `translate(0, ${chartHeight})`);

// let swoopy = swoopyDrag()
//   .x(d => x(d.xVal) + (frameWidth < breakPoint ? 5 : 20))
//   .y(d => y(d.yVal) + 35)
//   .draggable(false)
//   .annotations(frameWidth < breakPoint ? mobileAnnotations : annotations);

// swoopySel
//   .attr('transform', `translate(${margin.left},${margin.top})`)
//   .call(swoopy)
//   .attr('font-size', frameWidth < breakPoint ? '12px' : '13px');

// swoopySel.selectAll('text').each(function(d) {
//   d3.select(this)
//     .text('')
//     .attr('font-weight', d.weight)
//     .tspans(wordwrap(d.text, frameWidth < breakPoint ? 24 : 24));
// });

// swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');
